import { useState } from 'react';
import { useBrand } from '../lib/brand';
import {
  FlagGlyph,
  HeaderIconTile,
  HomeGlyph,
  PeopleGlyph,
  SessionsGlyph,
  HEADER_GREEN,
  HEADER_NAVY,
  HEADER_ORANGE,
  HEADER_TEAL,
} from '../components/publicHeaderIcons';
import { MarketingShell, SectionHeading, usePageMeta } from './chrome';
import { clubIntro, featureTabs, features } from './content';
import { ProductVisual } from './frames';

const TAB_MARKS = {
  roster: { color: HEADER_NAVY, icon: <PeopleGlyph /> },
  training: { color: HEADER_TEAL, icon: <SessionsGlyph /> },
  meets: { color: HEADER_ORANGE, icon: <FlagGlyph /> },
  club: { color: HEADER_GREEN, icon: <HomeGlyph /> },
} as const;

type FeatureTabId = (typeof featureTabs)[number]['id'];

export default function FeaturesPage() {
  const brand = useBrand();
  const [active, setActive] = useState<FeatureTabId>(featureTabs[0].id);
  const tab = featureTabs.find((item) => item.id === active) ?? featureTabs[0]!;
  usePageMeta(`Features · ${brand.name}`, clubIntro.sub);

  function onKeyDown(event: React.KeyboardEvent<HTMLDivElement>) {
    const index = featureTabs.findIndex((item) => item.id === active);
    if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
      event.preventDefault();
      const step = event.key === 'ArrowRight' ? 1 : -1;
      const next = featureTabs[(index + step + featureTabs.length) % featureTabs.length];
      if (next) {
        setActive(next.id);
        document.getElementById(`tab-${next.id}`)?.focus();
      }
    }
    if (event.key === 'Home') {
      event.preventDefault();
      const first = featureTabs[0];
      if (first) setActive(first.id);
    }
    if (event.key === 'End') {
      event.preventDefault();
      const last = featureTabs[featureTabs.length - 1];
      if (last) setActive(last.id);
    }
  }

  return (
    <MarketingShell>
      <section className="mkt-band mkt-band-tide" data-mkt-surface="dark">
        <div className="px-6 pb-8 pt-mkt-sm md:pt-mkt">
          <SectionHeading level="h1" tone="light" title={clubIntro.title} sub={clubIntro.sub} />
          <div
            role="tablist"
            aria-label="Feature areas"
            className="mx-auto mt-10 flex max-w-copy justify-center gap-3 overflow-x-auto pb-1"
            onKeyDown={onKeyDown}
          >
            {featureTabs.map((item) => {
              const selected = item.id === tab.id;
              const mark = TAB_MARKS[item.id];
              return (
                <button
                  key={item.id}
                  type="button"
                  role="tab"
                  id={`tab-${item.id}`}
                  aria-selected={selected}
                  aria-controls={`panel-${item.id}`}
                  tabIndex={selected ? 0 : -1}
                  className={`inline-flex h-14 shrink-0 items-center gap-2.5 rounded-full pl-2 pr-5 text-[16px] font-bold tracking-tight ${
                    selected ? 'bg-white text-[#1d1d1f] shadow-sm' : 'bg-white/20 text-white'
                  }`}
                  onClick={() => setActive(item.id)}
                >
                  <HeaderIconTile color={mark.color} size="sm">
                    {mark.icon}
                  </HeaderIconTile>
                  {item.label}
                </button>
              );
            })}
          </div>
        </div>
      </section>
      <article className="px-6 py-mkt-sm md:py-mkt">
        <div
          role="tabpanel"
          id={`panel-${tab.id}`}
          aria-labelledby={`tab-${tab.id}`}
          className="mx-auto grid max-w-[1100px] items-center gap-10 lg:grid-cols-2"
        >
          <div>
            <h2 className="text-balance text-mkt-section-sm md:text-[40px] md:font-semibold md:tracking-tight">{tab.title}</h2>
            <p className="mt-5 text-mkt-body text-mkt-secondary">{tab.body}</p>
            <ul className="mt-6 space-y-4">
              {tab.points.map((id) => {
                const feature = features.find((item) => item.id === id);
                if (!feature) return null;
                return (
                  <li key={id}>
                    <p className="text-mkt-body font-semibold">{feature.title}</p>
                    <p className="mt-1 text-mkt-body text-mkt-secondary">{feature.body}</p>
                  </li>
                );
              })}
            </ul>
          </div>
          <ProductVisual
            src={tab.image}
            alt={tab.alt}
            device={tab.device}
            tone={tab.tone}
            tilt={tab.tilt}
            companion={tab.companion}
            float={tab.float}
            avifSrc={tab.avif}
          />
        </div>
      </article>
    </MarketingShell>
  );
}
